import React from 'react';
import { Link } from 'react-router-dom';
import {
  Bullseye,
  GraphUpArrow,
  Eye,
  BarChartLine,
  Shop,
  Stars,
  PersonBadge,
  BoxSeam,
  Bag,
  Megaphone,
  Speedometer2,
} from 'react-bootstrap-icons';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { useLocale } from '../i18n/LocaleContext';
import './Home.scss';

export default function Home() {
  const { t } = useLocale();

  // "Why partner with Outfino?"
  const whys = [
    { key: 'audience', Icon: Bullseye },
    { key: 'intent', Icon: GraphUpArrow },
    { key: 'visibility', Icon: Eye },
    { key: 'insights', Icon: BarChartLine },
    { key: 'integration', Icon: Shop },
  ];

  // "Your Brand Dashboard" — what you can manage.
  const dashboard = [
    { key: 'profile', Icon: PersonBadge },
    { key: 'sync', Icon: BoxSeam },
    { key: 'track', Icon: BarChartLine },
    { key: 'campaigns', Icon: Megaphone },
    { key: 'performance', Icon: Speedometer2 },
  ];

  return (
    <div className="home">
      <Navbar />

      {/* ============ HERO ============ */}
      <section className="hero">
        <div className="hero__accent" aria-hidden="true" />
        <div className="hero__glow hero__glow--blue" aria-hidden="true" />
        <div className="hero__glow hero__glow--purple" aria-hidden="true" />
        <div className="hero__glow hero__glow--cream" aria-hidden="true" />
        <div className="container-x hero__inner">
          <Reveal as="span" className="hero__badge" delayIndex={0}>
            <Stars size={13} />
            {t('hero.badge')}
          </Reveal>
          <Reveal as="h1" className="hero__title" delayIndex={1}>
            {t('hero.title')}
          </Reveal>
          <Reveal as="p" className="hero__subtitle" delayIndex={2}>
            {t('hero.subtitle')}
          </Reveal>
          <Reveal className="hero__cta" delayIndex={3}>
            <Link to="/login" className="btn-o btn-o--dark">
              {t('hero.ctaLogin')}
            </Link>
            <a href="#why" className="btn-o btn-o--brand">
              {t('hero.ctaLearn')}
            </a>
          </Reveal>
        </div>
      </section>

      {/* ============ WHAT IT DOES ============ */}
      <section id="what" className="section what">
        <div className="container-x">
          <Reveal as="span" className="eyebrow">
            {t('what.eyebrow')}
          </Reveal>
          <Reveal as="h2" className="what__title" delayIndex={1}>
            {t('what.title')}
          </Reveal>
          <Reveal as="p" className="what__body" delayIndex={2}>
            {t('what.body')}
          </Reveal>
        </div>
      </section>

      {/* ============ WHY PARTNER ============ */}
      <section id="why" className="section section--tint">
        <div className="container-x">
          <div className="section-head">
            <Reveal as="span" className="eyebrow">
              {t('why.eyebrow')}
            </Reveal>
            <Reveal as="h2" className="section-title" delayIndex={1}>
              {t('why.title')}
            </Reveal>
          </div>
          <div className="feature-grid">
            {whys.map(({ key, Icon }, i) => (
              <Reveal as="article" className="feature-card" key={key} delayIndex={i}>
                <span className="feature-card__icon">
                  <Icon size={22} />
                </span>
                <h3 className="feature-card__title">{t(`why.${key}.title`)}</h3>
                <p className="feature-card__body">{t(`why.${key}.body`)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ YOUR BRAND DASHBOARD ============ */}
      <section id="dashboard" className="section">
        <div className="container-x">
          <div className="section-head">
            <Reveal as="span" className="eyebrow">
              {t('dash.eyebrow')}
            </Reveal>
            <Reveal as="h2" className="section-title" delayIndex={1}>
              {t('dash.title')}
            </Reveal>
            <Reveal as="p" className="section-sub" delayIndex={2}>
              {t('dash.subtitle')}
            </Reveal>
          </div>
          <div className="why-grid">
            {dashboard.map(({ key, Icon }, i) => (
              <Reveal className="why-item" key={key} delayIndex={i}>
                <span className="why-item__icon">
                  <Icon size={20} />
                </span>
                <div>
                  <h3 className="why-item__title">{t(`dash.${key}.title`)}</h3>
                  <p className="why-item__body">{t(`dash.${key}.body`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section id="cta" className="section cta">
        <div className="container-x">
          <Reveal className="cta__card">
            <div className="cta__glow" aria-hidden="true" />
            <h2 className="cta__title">{t('cta.title')}</h2>
            <p className="cta__body">{t('cta.body')}</p>
            <div className="cta__actions">
              <Link to="/login" className="btn-o btn-o--light">
                <Bag size={16} style={{ marginRight: 8 }} />
                {t('cta.become')}
              </Link>
              <a
                href="mailto:sales@outfino.io?subject=Outfino%20Partner"
                className="btn-o btn-o--brand"
              >
                {t('cta.contactSales')}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
